import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Profile.css';
import '../css/Inventory.css';
import '../css/EventPage.css';
import '../css/FantasyTeamStats.css';
import { useLogOut } from '../hooks/useLogOut';
import { useUser } from '../hooks/useUser';
import { useUserByNumber } from '../hooks/useUserByNumber';
import { useUserStatsByUserID } from '../hooks/useUserStatsByUserID';
import { useFantasyTeams } from '../hooks/useFantasyTeams';
import { useSeason } from '../hooks/useSeason';
import { useTeam } from '../hooks/useTeam';
import { usePlayer } from '../hooks/usePlayer';
import { ThemeContext } from '../context/ThemeContext';
import PortalSearchSuggestions from './PortalSearchSuggestions';
import ChangeUsername from './ChangeUsername';
import FriendList from './FriendList';
import Inventory from './Inventory';
import PortalDropdown from './PortalDropdown';
import FantasyTeamView from './FantasyTeamView';
import FantasyTeamStats from './FantasyTeamStats';
import { AchievementGrid } from './AchievementCard';
import UnkownPlayer from '../assets/unknownPlayer.png';
import UnknownTeam from '../assets/Unknown.png';
import { useInventory } from '../hooks/fetchInventory';
import { useBank } from '../hooks/useBank';
import { useCosmetics } from '../hooks/useCosmetics';
import { useAchievements } from '../hooks/useAchievements';
import { useFantasyTeamStats } from '../hooks/useFantasyTeamStats';
import { useFriends } from '../hooks/useFriends';

const Profile = () => {
  const { userNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current view based on URL
  const getCurrentView = () => {
    if (location.pathname.includes('/inventory')) return 'inventory';
    if (location.pathname.includes('/fantasy-teams')) return 'fantasyTeams';
    if (location.pathname.includes('/achievements')) return 'achievements';
    return 'profile';
  };

  const currentView = getCurrentView();

  const { user: currentUser, favorites, favoritesLoading, fetchFavorites, updateFavoriteTeam, updateFavoritePlayer } = useUser(userNumber);
  const { user: otherUser, loading: otherUserLoading, error: otherUserError } = useUserByNumber(userNumber);
  const { logOut } = useLogOut();
  const { changeTheme, theme, applyUserTheme } = useContext(ThemeContext);
  const { fetchSeasons, seasons } = useSeason();
  const { fetchAllTeams, teams } = useTeam();
  const { fetchAllPlayers, players } = usePlayer();
  const { fetchInventorySizeByUserNumber, inventorySize, clearInventoryCache } = useInventory();
  const { fetchCoinsByUserNumber, coins } = useBank();

  // Determine which user to display
  const isViewingOtherUser = currentUser?._id !== otherUser?._id;
  const displayUser = isViewingOtherUser ? otherUser : currentUser;

  // Only fetch current user's cosmetics if viewing own profile
  const cosmeticsResult = useCosmetics();
  const {
    cosmetics,
    loading: cosmeticsLoading,
    getCosmeticsByType,
    updateSelectedCardBackground,
    updateSelectedCardOutline,
    selectedCardBackground,
    selectedCardOutline,
    getUserSelectedCardBackground,
    getUserSelectedCardOutline
  } = !isViewingOtherUser ? cosmeticsResult : {
    cosmetics: [],
    loading: false,
    getCosmeticsByType: () => [],
    updateSelectedCardBackground: () => { },
    updateSelectedCardOutline: () => { },
    selectedCardBackground: null,
    selectedCardOutline: null,
    getUserSelectedCardBackground: () => { },
    getUserSelectedCardOutline: () => { }
  };

  // Friends hook for friend management
  const {
    checkFriendshipStatus,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    removeFriend
  } = useFriends();

  // Season selection state
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [selectedSeasonName, setSelectedSeasonName] = useState('');
  const [searchTeam, setSearchTeam] = useState('');
  const [searchPlayer, setSearchPlayer] = useState('');
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  const [showFriendList, setShowFriendList] = useState(false);

  // Friend status state
  const [friendStatus, setFriendStatus] = useState(null);
  const [friendStatusLoading, setFriendStatusLoading] = useState(false);

  // Fetch stats based on selected season
  const { userStats: UserStats, loading: StatsLoading, error: StatsError } = useUserStatsByUserID(otherUser?._id, selectedSeasonId);

  const { fantasyTeams, loading: fantasyTeamsLoading, refetch: refetchFantasyTeams } = useFantasyTeams(
    displayUser?._id // Always provide the user ID
  );
  const [eventsParticipated, setEventsParticipated] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [totalBoostPoints, setTotalBoostPoints] = useState(0);
  const [averagePoints, setAveragePoints] = useState(0);
  const [averageBoostPoints, setAverageBoostPoints] = useState(0);
  const [winPercentage, setWinPercentage] = useState(0);

  // Compute currentFantasyTeam directly instead of using state
  const currentFantasyTeam = useMemo(() => {
    if (!fantasyTeams || fantasyTeams.length === 0) return null;

    if (selectedSeasonId === 'all-time') {
      return fantasyTeams;
    } else if (selectedSeasonId) {
      return fantasyTeams.filter(team => team.season === selectedSeasonId);
    } else {
      return fantasyTeams;
    }
  }, [fantasyTeams, selectedSeasonId]);

  const [otherUserCardBackground, setOtherUserCardBackground] = useState(null);
  const [otherUserCardOutline, setOtherUserCardOutline] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force re-render when card background changes

  // Achievements hook
  const {
    achievements,
    loading: achievementsLoading,
    error: achievementsError,
    stats: achievementStats,
    fetchUserAchievements
  } = useAchievements(displayUser?._id, selectedSeasonId);

  // Fantasy team stats hook
  const {
    stats: fantasyStats,
    loading: fantasyStatsLoading,
    error: fantasyStatsError,
    refetch: refetchFantasyStats
  } = useFantasyTeamStats(userNumber, currentView === 'achievements');

  // Use useRef to track what's been fetched to prevent redundant API calls
  const fetchedRef = useRef({
    seasons: false,
    teams: false,
    players: false,
    userSpecific: null // Track by userNumber
  });

  // Helper functions used in useMemo - moved here to avoid hooks order issues
  const formatDate = useCallback((unixTimestamp) => {
    const date = new Date(unixTimestamp);
    return date.toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Calculate font size for username based on its length
  const getUsernameFontSize = (username) => {
    if (!username) return '1.4vw';
    if (username.length <= 8) return '1.4vw';

    return (1.4 - username.length * 0.01) + 'vw';
  };

  const findSeasonNameByDate = useCallback((date) => {
    const parsedDate = new Date(date);
    const season = seasons?.find(season => {
      const start = new Date(season.start);
      const end = new Date(season.end);
      return parsedDate >= start && parsedDate <= end;
    });
    return season ? season.name : 'Unknown Season';
  }, [seasons]);

  const handleTeamSelection = useCallback((teamName) => {
    const selectedTeam = teams.find(team => team.name === teamName);
    if (selectedTeam) {
      updateFavoriteTeam(selectedTeam._id);
      setSearchTeam('');
    }
  }, [teams, updateFavoriteTeam]);

  const handlePlayerSelection = useCallback((playerName) => {
    const selectedPlayer = players.find(player => player.name === playerName);
    if (selectedPlayer) {
      updateFavoritePlayer(selectedPlayer._id);
      setSearchPlayer('');
    }
  }, [players, updateFavoritePlayer]);

  // Check friendship status when viewing other user
  const checkFriendStatus = useCallback(async () => {
    if (!isViewingOtherUser || !otherUser?.userNumber) return;

    try {
      setFriendStatusLoading(true);
      const status = await checkFriendshipStatus(otherUser.userNumber);
      setFriendStatus(status);
    } catch (error) {
      console.error('Error checking friend status');
      setFriendStatus(null);
    } finally {
      setFriendStatusLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingOtherUser, otherUser?.userNumber, checkFriendshipStatus]);

  // Handle friend actions
  const handleFriendAction = useCallback(async (action) => {
    if (!otherUser?._id) return;

    try {
      setFriendStatusLoading(true);

      switch (action) {
        case 'send':
          await sendFriendRequest(otherUser._id);
          break;
        case 'accept':
          await acceptFriendRequest(otherUser._id);
          break;
        case 'decline':
          await declineFriendRequest(otherUser._id);
          break;
        case 'cancel':
          await cancelFriendRequest(otherUser._id);
          break;
        case 'remove':
          if (window.confirm('Are you sure you want to remove this friend?')) {
            await removeFriend(otherUser._id);
          }
          break;
        default:
          throw new Error('Invalid action');
      }

      // Refresh friend status after action
      await checkFriendStatus();
    } catch (error) {
      console.error('Error with friend action');
      alert(`Error: ${error.message}`);
    } finally {
      setFriendStatusLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUser?._id, sendFriendRequest, acceptFriendRequest, declineFriendRequest, cancelFriendRequest, removeFriend, checkFriendStatus]);


  const onChangeUsername = () => {
    setShowChangeUsername(true);
  }

  const handleLogOut = async (e) => {
    const success = await logOut();
    if (success) {
      window.location.href = '/'; // Force full reload to clear all state
    }
  }


  // Render friend action buttons
  const renderFriendButton = () => {
    if (!isViewingOtherUser || friendStatusLoading) {
      return (
        <button className='default-button profile-nav-button' disabled>
          {friendStatusLoading ? 'Loading...' : 'Loading'}
        </button>
      );
    }

    if (!friendStatus) {
      return (
        <button className='default-button profile-nav-button' disabled>
          Unable to load
        </button>
      );
    }

    switch (friendStatus.status) {
      case 'friends':
        return (
          <button
            className='default-button profile-nav-button'
            onClick={() => handleFriendAction('remove')}
          >
            Remove Friend
          </button>
        );
      case 'sent':
        return (
          <button
            className='default-button profile-nav-button'
            onClick={() => handleFriendAction('cancel')}
          >
            Cancel Request
          </button>
        );
      case 'received':
        return (
          <div style={{ display: 'flex', gap: '0.5vw' }}>
            <button
              className='default-button'
              style={{ width: '50%' }}
              onClick={() => handleFriendAction('accept')}
            >
              Accept
            </button>
            <button
              className='default-button'
              style={{ width: '50%' }}
              onClick={() => handleFriendAction('decline')}
            >
              Decline
            </button>
          </div>
        );
      case 'none':
      default:
        return (
          <button
            className='default-button profile-nav-button'
            onClick={() => handleFriendAction('send')}
          >
            Add Friend
          </button>
        );
    }
  };

  // Memoized profile information - only re-renders when dependencies change
  const profileInformation = useMemo(() => {
    return (
      <div className='profile-information-main-container'>
        <h3 className='title-inventory'>Information</h3>
        <div className='profile-information-container'>
          <div className='profile-image-container'>
            <img className='profile-image-team' alt='' src={favorites?.favoriteTeam ? teams.find(team => team._id === favorites.favoriteTeam._id)?.image : UnknownTeam} />
            <img className='profile-image-player' alt='' src={favorites?.favoritePlayer ? players.find(player => player._id === favorites.favoritePlayer._id)?.image : UnkownPlayer} />
          </div>
          <div className='profile-image-options-container'>
            <div className='profile-image-options'>
              <div className='market-search'>
                <h6 className='title-inventory'>Favorite Team</h6>
                <PortalSearchSuggestions
                  query={teams}
                  searchTerm={searchTeam}
                  setSearchTerm={(teamName) => {
                    setSearchTeam(teamName);
                    handleTeamSelection(teamName);
                  }}
                  suggestionsLength={10}
                  zIndex={9999}
                  placeholder={favorites?.favoriteTeam ? favorites.favoriteTeam.name : "No Favorite Team"}
                  disabled={isViewingOtherUser}
                />
              </div>
              <div className='market-search'>
                <h6 className='title-inventory'>Favorite Player</h6>
                <PortalSearchSuggestions
                  query={players}
                  searchTerm={searchPlayer}
                  setSearchTerm={(playerName) => {
                    setSearchPlayer(playerName);
                    handlePlayerSelection(playerName);
                  }}
                  suggestionsLength={10}
                  zIndex={9998}
                  placeholder={favorites?.favoritePlayer ? favorites.favoritePlayer.name : "No Favorite Player"}
                  disabled={isViewingOtherUser}
                />
              </div>
            </div>
          </div>
          <div className='profile-stats-table-container profile-info-table'>
            <table className='profile-stats-table'>
              <thead>
                <tr>
                  <th style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: getUsernameFontSize(displayUser?.username) }}><span>{displayUser?.username}</span></th>


                  <th style={{ display: 'flex', flexDirection: 'row', fontSize: '1vw', justifyContent: 'flex-start' }}>
                    {displayUser?.isPremium && <span className='profile-user-tag'>Premium</span>}
                    {isViewingOtherUser && friendStatus && (
                      <>
                        {friendStatus.status === 'friends' && (
                          <span className='profile-user-tag friend'>Friend</span>
                        )}
                        {friendStatus.status === 'sent' && (
                          <span className='profile-user-tag sent'>Request Sent</span>
                        )}
                        {friendStatus.status === 'received' && (
                          <span className='profile-user-tag received'>Request Received</span>
                        )}
                        {friendStatus.status === 'none' && (
                          <span className='profile-user-tag not-friend'>Not Friends</span>
                        )}
                      </>
                    )}
                  </th>

                </tr>
              </thead>
              <tbody className='small'>
                <tr>
                  <td>League</td>
                  <td>{displayUser && displayUser.league ? displayUser.league.name : 'No League'}</td>
                </tr>
                <tr>
                  <td>Date Joined</td>
                  <td>{displayUser && formatDate(displayUser?.dateJoined)} ({findSeasonNameByDate(displayUser?.dateJoined)})</td>
                </tr>
                <tr>
                  <td>Inventory Size</td>
                  <td>{inventorySize ? inventorySize : '0'}</td>
                </tr>
                <tr>
                  <td>Coins</td>
                  <td>{coins ? coins : '0'}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {!isViewingOtherUser ? (
            <div className='profile-information-button-container'>
              <button className='default-button profile-nav-button' onClick={onChangeUsername}>
                Change Username
              </button>
              <button className='default-button profile-nav-button' onClick={() => navigate(`/change-password`)}>
                Change Password
              </button>
              <button className='default-button profile-nav-button' onClick={() => setShowFriendList(true)}>
                Friend List
              </button>
            </div>
          ) : (
            <div className='profile-information-button-container'>
              {renderFriendButton()}
              <button className='default-button profile-nav-button' disabled={true}>
                Trade
              </button>
              <button className='default-button profile-nav-button' onClick={() => setShowFriendList(true)}>
                Friend List
              </button>
            </div>
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    favorites,
    teams,
    players,
    displayUser,
    searchTeam,
    searchPlayer,
    inventorySize,
    coins,
    isViewingOtherUser,
    seasons,
    friendStatus
  ]);

  // Apply other user's theme when viewing their profile
  useEffect(() => {
    if (isViewingOtherUser && otherUser?.userNumber) {
      applyUserTheme(userNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingOtherUser, userNumber, applyUserTheme]);

  // Check friendship status when viewing other user
  useEffect(() => {
    if (isViewingOtherUser && otherUser?.userNumber) {
      checkFriendStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingOtherUser, otherUser?.userNumber]);

  // Fetch other user's card background and outline when viewing their profile
  useEffect(() => {
    if (isViewingOtherUser && userNumber) {
      const fetchOtherUserCosmetics = async () => {
        try {
          const [cardBackground, cardOutline] = await Promise.all([
            getUserSelectedCardBackground(userNumber),
            getUserSelectedCardOutline(userNumber)
          ]);
          setOtherUserCardBackground(cardBackground);
          setOtherUserCardOutline(cardOutline);
        } catch (error) {
          setOtherUserCardBackground(null);
          setOtherUserCardOutline(null);
          console.error('Error fetching other user cosmetics');
        }
      };
      fetchOtherUserCosmetics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingOtherUser, userNumber]);

  // Refetch achievements when season or user changes
  useEffect(() => {
    if (displayUser?._id && currentView === 'achievements') {
      fetchUserAchievements(displayUser._id, selectedSeasonId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayUser?._id, selectedSeasonId, currentView]);

  useEffect(() => {
    if (fantasyTeams) {
      // Filter teams based on selected season
      let finnishedFantasyTeams;
      if (selectedSeasonId === 'all-time' || selectedSeasonId === null) {
        finnishedFantasyTeams = fantasyTeams.filter(team => team.active === false);
      } else {
        finnishedFantasyTeams = fantasyTeams.filter(team => team.active === false && team.season === selectedSeasonId);
      }

      setEventsParticipated(finnishedFantasyTeams.length);
      let points = 0;
      let boostPoints = 0;

      for (const team of finnishedFantasyTeams) {
        points += team.points;
        boostPoints += team.boostPoints;
      }

      setTotalPoints(points);
      setTotalBoostPoints(boostPoints);
      setAveragePoints(finnishedFantasyTeams.length > 0 ? points / finnishedFantasyTeams.length : 0);
      setAverageBoostPoints(finnishedFantasyTeams.length > 0 ? boostPoints / finnishedFantasyTeams.length : 0);
      setWinPercentage(finnishedFantasyTeams.length > 0 ? (UserStats?.eventsWon / finnishedFantasyTeams.length) * 100 : 0);
    }
  }, [fantasyTeams, UserStats, selectedSeasonId]);

  // Memoize cosmetic arrays to prevent unnecessary re-renders
  const memoizedCosmetics = useMemo(() => ({
    themes: getCosmeticsByType('theme'),
    cardBackgrounds: getCosmeticsByType('cardBackground'),
    cardOutlines: getCosmeticsByType('cardOutline')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [getCosmeticsByType, cosmetics]);

  useEffect(() => {
    const themeButton = document.getElementById('theme-dropdown-button');
    if (themeButton && theme) {
      themeButton.textContent = `Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`;
    }

    const cardBackgroundButton = document.getElementById('card-background-dropdown-button');
    if (cardBackgroundButton && selectedCardBackground) {
      cardBackgroundButton.textContent = `Card Background: ${selectedCardBackground.name}`;
    }

    const cardOutlineButton = document.getElementById('card-outline-dropdown-button');
    if (cardOutlineButton) {
      if (selectedCardOutline && selectedCardOutline.name) {
        cardOutlineButton.textContent = `Card Outline: ${selectedCardOutline.name}`;
      } else {
        cardOutlineButton.textContent = 'Card Outline: None';
      }
    }

  }, [theme, selectedCardBackground, selectedCardOutline]);

  useEffect(() => {
    // Only fetch global data once
    if (!fetchedRef.current.seasons && (!seasons || seasons.length === 0)) {
      fetchSeasons();
      fetchedRef.current.seasons = true;
    }
    if (!fetchedRef.current.teams && (!teams || teams.length === 0)) {
      fetchAllTeams();
      fetchedRef.current.teams = true;
    }
    if (!fetchedRef.current.players && (!players || players.length === 0)) {
      fetchAllPlayers();
      fetchedRef.current.players = true;
    }

    // Only fetch user-specific data when userNumber changes
    if (fetchedRef.current.userSpecific !== userNumber) {
      fetchFavorites(userNumber);
      fetchInventorySizeByUserNumber(userNumber);
      fetchCoinsByUserNumber(userNumber);
      fetchedRef.current.userSpecific = userNumber;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userNumber, seasons, teams, players]);

  const updateTheme = (theme) => {
    changeTheme(theme);
  }

  const updateCardBackground = async (cardBackgroundId) => {
    try {
      // Wait for the card background to be updated in the backend first
      await updateSelectedCardBackground(cardBackgroundId);

      // Now clear cache and refetch with the new card background
      clearInventoryCache();

      // Refetch both fantasy team stats and fantasy teams data in parallel
      await Promise.all([
        refetchFantasyStats(),
        refetchFantasyTeams()
      ]);

      // Then increment refresh key to force re-render of components
      setRefreshKey(prev => prev + 1);

    } catch (error) {
      console.error('Error updating card background');
    }
  }

  const updateCardOutline = async (cardOutlineId) => {
    try {
      // Wait for the card outline to be updated in the backend first
      await updateSelectedCardOutline(cardOutlineId);

      // Now clear cache and refetch with the new card outline
      clearInventoryCache();

      // Refetch both fantasy team stats and fantasy teams data in parallel
      await Promise.all([
        refetchFantasyStats(),
        refetchFantasyTeams()
      ]);

      // Then increment refresh key to force re-render of components
      setRefreshKey(prev => prev + 1);

    } catch (error) {
      console.error('Error updating card outline');
    }
  }

  const handleSeasonChange = (seasonId) => {
    setSelectedSeasonId(seasonId);
    setSelectedSeasonName(seasonId === 'all-time' ? 'All Time' : seasons.find(season => season._id === seasonId)?.name || '');
  }

  // View change handlers
  const handleViewChange = (view) => {
    switch (view) {
      case 'profile':
        navigate(`/profile/${userNumber}`);
        break;
      case 'inventory':
        navigate(`/profile/${userNumber}/inventory`);
        break;
      case 'fantasyTeams':
        navigate(`/profile/${userNumber}/fantasy-teams`);
        break;
      case 'achievements':
        navigate(`/profile/${userNumber}/achievements`);
        break;
      default:
        break;
    }
  };

  // Show loading state
  if (!otherUserError && (otherUserLoading || favoritesLoading)) {
    return (
      <div className='main-container col-10 offset-1'>
        <div className='loader-overlay'>
          <div className='loader'></div>
        </div>
      </div>
    );
  }

  // Show error state
  if (isViewingOtherUser && otherUserError) {
    return (
      <div className='main-container col-10 offset-1'>
        <div className='title-container'>
          <div className='inventory-box'>
            <h1 className='title'>Profile</h1>
          </div>
          <div className='profile-options'>
            <button className='default-button' onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
        <div className='profile-information-main-container text-center justify-content-center' style={{ minHeight: '30vh' }}>
          <h4>{otherUserError}</h4>
        </div>
      </div>
    );
  }

  // Render different content based on current view
  const renderContent = () => {
    switch (currentView) {
      case 'inventory':
        return (
          <>
            <div className='scroll-container'>
              <div>
                {profileInformation}
                <div className='event-page-inventory-container' style={{ position: 'relative' }}>
                  <Inventory
                    userNumber={userNumber}
                    fullSize={true}
                    key={`inventory-${refreshKey}`}
                  />
                </div>
              </div>
            </div>
          </>
        );
      case 'fantasyTeams':
        return (
          <>
            <div className='scroll-container'>
              <div>
                {profileInformation}

                <div className='title-container' style={userNumber && { top: '0.5vw' }}>
                  <div className=' inventory-box' style={{ width: '50%' }}>
                    <h1 className='title'>Fantasy Teams</h1>
                  </div>
                  <div className='inventory-box-filter'>
                    <PortalDropdown
                      buttonText={selectedSeasonName || 'Select Season'}
                      buttonProps={{ id: "dropdown-button" }}
                    >
                      <p onClick={() => handleSeasonChange('all-time')}>All Time</p>
                      {seasons && seasons.length > 0 ? (
                        seasons.map(season => (
                          <p key={season._id} onClick={() => handleSeasonChange(season._id)}>
                            {season.name}
                          </p>
                        ))
                      ) : (
                        <div className='loader-small' />
                      )}
                    </PortalDropdown>
                  </div>
                </div>

                {currentFantasyTeam && currentFantasyTeam.length > 0 ? (
                  <div className='fantasy-teams-container'>
                    {currentFantasyTeam.map(team => (
                      <FantasyTeamView
                        key={`${team._id}-${refreshKey}`}
                        parsedFantasyTeam={team}
                      />
                    ))}
                  </div>
                ) : (
                  <div className='profile-information-main-container'>
                    <div className='fantasyTeamView-content' style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {fantasyTeamsLoading ? (
                        <div className='loader' />
                      ) : (
                        <p className='text-center'>No fantasy teams found for this season.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      case 'achievements':
        return (
          <>
            <div className='scroll-container'>
              {profileInformation}

              {/* Fantasy Team Stats */}
              <FantasyTeamStats
                fantasyStats={fantasyStats}
                loading={fantasyStatsLoading}
                error={fantasyStatsError}
                userNumber={userNumber}
                userId={displayUser?._id}
                key={`fantasy-stats-${refreshKey}`}
              />

              <div className='title-container' style={{ position: 'relative', marginTop: '0.5vw' }}>
                <div className=' inventory-box' style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                  <h1 className='title'>Achievements</h1>
                  <div className='teamWinner-box' style={{ justifyContent: 'center', width: 'auto', fontSize: '1vw' }}>
                    {achievementStats?.completed} / {achievementStats?.total} ({achievementStats?.completionRate.toFixed(1)}%)
                  </div>
                </div>
              </div>

              <div className='profile-information-main-container'>
                <div className='profile-information-container'>
                  {/* Achievement Grid */}
                  <div className='market-filter-inner'>
                    <AchievementGrid
                      achievements={achievements}
                      loading={achievementsLoading}
                      error={achievementsError}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case 'profile':
      default:
        return (
          <>
            {profileInformation}
            <div className='profile-stats-main-container'>
              <div className='profile-stats-header'>
                <h3 className='title-inventory'>Stats</h3>
                <div className='inventory-box-filter'>
                  <PortalDropdown
                    buttonText={selectedSeasonName || 'Select Season'}
                    buttonProps={{ id: "dropdown-button" }}
                  >
                    <p onClick={() => handleSeasonChange('all-time')}>All Time</p>
                    {seasons && seasons.length > 0 ? (
                      seasons.map(season => (
                        <p key={season._id} onClick={() => handleSeasonChange(season._id)}>
                          {season.name}
                        </p>
                      ))
                    ) : (
                      <div className='loader-small' />
                    )}
                  </PortalDropdown>
                </div>
              </div>
              {StatsError ? (
                <div className='error text-center justify-content-center'>
                  <p>Unable to load user statistics: {StatsError}</p>
                </div>
              ) : (
                <div className='profile-stats-container'>
                  {/* Your existing stats tables */}
                  <div className='profile-stats-table-container'>
                    <table className='profile-stats-table'>
                      {StatsLoading && (
                        <div className='loader-overlay'>
                          <div className='loader'></div>
                        </div>
                      )}
                      <thead>
                        <tr>
                          <th>Stat</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSeasonId !== 'all-time' && selectedSeasonId !== null && UserStats?.finalPlacement && UserStats?.finalPoints && UserStats?.finalPointsFromFirstPlace && (
                          <tr>
                            <td style={{ fontSize: '1.1vw' }}>Final Placement</td>
                            <td style={{ fontSize: '1.1vw' }}>{UserStats?.finalPlacement ?? 0} ({UserStats?.finalPoints ?? 0} Points)</td>
                          </tr>
                        )}
                        <tr>
                          <td>Packs Opened</td>
                          <td>{UserStats?.packsOpened ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Cards Opened</td>
                          <td>{UserStats?.cardsOpened ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Bronze Opened</td>
                          <td>{UserStats?.bronzeOpened ?? 0} ({UserStats?.bronzePercentage?.toFixed(2) ?? 0}%)</td>
                        </tr>
                        <tr>
                          <td>Silver Opened</td>
                          <td>{UserStats?.silverOpened ?? 0} ({UserStats?.silverPercentage?.toFixed(2) ?? 0}%)</td>
                        </tr>
                        <tr>
                          <td>Gold Opened</td>
                          <td>{UserStats?.goldOpened ?? 0} ({UserStats?.goldPercentage?.toFixed(2) ?? 0}%)</td>
                        </tr>
                        <tr>
                          <td>Legendaries Opened</td>
                          <td>{UserStats?.legendariesOpened ?? 0} ({UserStats?.legendaryPercentage?.toFixed(2) ?? 0}%)</td>
                        </tr>
                        <tr>
                          <td>TBC Completed</td>
                          <td>{UserStats?.tbcCompleted ?? 0}  </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className='profile-stats-table-container'>
                    <table className='profile-stats-table'>
                      <thead>
                        <tr>
                          <th>Stat</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSeasonId !== 'all-time' && selectedSeasonId !== null && UserStats?.finalPlacement && UserStats?.finalPoints && UserStats?.finalPointsFromFirstPlace && (
                          <tr>
                            <td style={{ fontSize: '1.1vw' }}>Final Points from 1st</td>
                            <td style={{ fontSize: '1.1vw' }}>{UserStats?.finalPointsFromFirstPlace ?? 0}</td>
                          </tr>
                        )}
                        <tr>
                          <td>Events Participated</td>
                          <td>{eventsParticipated ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Average Placement</td>
                          <td>{UserStats?.averagePlacement?.toFixed(2) ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Events Won</td>
                          <td>{UserStats?.eventsWon ?? 0} ({winPercentage.toFixed(2)}%)</td>
                        </tr>
                        <tr>
                          <td>Total Points</td>
                          <td>{totalPoints?.toFixed(2) ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Average Points</td>
                          <td>{averagePoints?.toFixed(2) ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Total Boost Points</td>
                          <td>{totalBoostPoints?.toFixed(2) ?? 0}</td>
                        </tr>
                        <tr>
                          <td>Average Boost Points</td>
                          <td>{averageBoostPoints?.toFixed(2) ?? 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        );
    }
  };

  return (
    <div className='main-container col-10 offset-1'>
      <div className='title-container'>
        <div className='inventory-box'>
          <h1 className='title'>Profile</h1>
          <div className="view-button-container">
            <div className="col-auto">
              <button
                className={`view-button ${currentView === 'profile' ? 'active' : ''}`}
                onClick={() => handleViewChange('profile')}
              >
                Profile
              </button>
            </div>
            <div className="col-auto">
              <button
                className={`view-button ${currentView === 'fantasyTeams' ? 'active' : ''}`}
                onClick={() => handleViewChange('fantasyTeams')}
              >
                Fantasy Teams
              </button>
            </div>
            <div className="col-auto">
              <button
                className={`view-button ${currentView === 'achievements' ? 'active' : ''}`}
                onClick={() => handleViewChange('achievements')}
              >
                Records & Achievements
              </button>
            </div>
            {isViewingOtherUser && (
              <div className="col-auto">
                <button
                  className={`view-button ${currentView === 'inventory' ? 'active' : ''}`}
                  onClick={() => handleViewChange('inventory')}
                >
                  Inventory
                </button>
              </div>
            )}
          </div>
        </div>
        <div className='profile-options'>
          {!isViewingOtherUser && (
            <div style={{ position: 'sticky', zIndex: '99999', display: 'flex' }}>

              <div className='inventory-box-filter'>
                <PortalDropdown
                  buttonText="Select Card Background"
                  buttonStyle={{ width: '17vw' }}
                  buttonProps={{ id: "card-background-dropdown-button" }}
                >
                  {cosmeticsLoading ? (
                    <div className='loader-small' />
                  ) : (
                    memoizedCosmetics.cardBackgrounds && (
                      memoizedCosmetics.cardBackgrounds.map(background => (
                        <p key={background._id} onClick={() => updateCardBackground(background._id)}>
                          {background.name}
                        </p>
                      ))
                    )
                  )}
                </PortalDropdown>

                <PortalDropdown
                  buttonText="Select Card Outline"
                  buttonProps={{ id: "card-outline-dropdown-button" }}
                >
                  {!cosmeticsLoading && (
                    <p key="reset-outline" onClick={() => updateCardOutline(null)}>
                      None
                    </p>
                  )}
                  {cosmeticsLoading ? (
                    <div className='loader-small' />
                  ) : (
                    memoizedCosmetics.cardOutlines && (
                      memoizedCosmetics.cardOutlines.map(outline => (
                        <p key={outline._id} onClick={() => updateCardOutline(outline._id)}>
                          {outline.name}
                        </p>
                      ))
                    )
                  )}
                </PortalDropdown>

                <PortalDropdown
                  buttonText="Select Theme"
                  buttonProps={{ id: "theme-dropdown-button" }}
                >
                  {cosmeticsLoading ? (
                    <div className='loader-small' />
                  ) : (
                    memoizedCosmetics.themes && memoizedCosmetics.themes.length === 0 ? (
                      <p>No themes available</p>
                    ) : (
                      memoizedCosmetics.themes.map(theme => (
                        <p key={theme._id} onClick={() => updateTheme(theme._id)}>
                          {theme.name}
                        </p>
                      ))
                    )
                  )}
                </PortalDropdown>
              </div>
              <button className='default-button' onClick={handleLogOut}>
                Log Out
              </button>
            </div>
          )}
          {isViewingOtherUser && (
            <div className='inventory-box-filter'>
              <div className="dropdown">
                <button id="card-background-dropdown-button" className="dropdown-button" style={{ width: '17vw' }} disabled>
                  {otherUserCardBackground ? `${otherUserCardBackground.name}` : "Card Background: Default"}
                </button>
              </div>
              <div className="dropdown">
                <button id="card-outline-dropdown-button" className="dropdown-button" style={{ width: '17vw' }} disabled>
                  {otherUserCardOutline ? `Card Outline: ${otherUserCardOutline.name}` : "Card Outline: None"}
                </button>
              </div>
              <div className="dropdown">
                <button id="theme-dropdown-button" className="dropdown-button" disabled>
                  {theme ? `Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)}` : 'Theme: Anubis'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {renderContent()}

      {showChangeUsername && (
        <div className='profile-overlay' onClick={() => setShowChangeUsername(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ChangeUsername onClose={() => setShowChangeUsername(false)} />
          </div>
        </div>
      )}

      {showFriendList && (
        <FriendList
          onClose={() => setShowFriendList(false)}
          isOwnProfile={!isViewingOtherUser}
          userNumber={userNumber}
        />
      )}
    </div>
  );
};

export default Profile;
